import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, HelpCircle, GitBranch, ShieldCheck, Check, X, Edit3,
  Trash2, CornerDownRight, Clock, Pencil,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import CommentComposer from "@/components/CommentComposer";
import ConfirmDialog from "@/components/ConfirmDialog";
import AttachmentPreview from "@/components/AttachmentPreview";
import { useAuth } from "@/context/AuthContext";

const TYPE_STYLE = {
  comment: {
    bar: "border-l-line",
    badge: null,
    icon: MessageSquare,
  },
  question: {
    bar: "border-l-ochre",
    badge: { text: "Pitanje", cls: "bg-ochre/12 text-ochre-ink border-ochre/30" },
    icon: HelpCircle,
  },
  decision: {
    bar: "border-l-forest",
    badge: { text: "Odluka", cls: "bg-forest/10 text-forest border-forest/30" },
    icon: GitBranch,
  },
  approval: {
    bar: "border-l-forest",
    badge: { text: "Odobrenje", cls: "bg-forest/10 text-forest border-forest/30" },
    icon: ShieldCheck,
  },
};

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.round((now - d) / 60000);
  if (diff < 1) return "upravo sad";
  if (diff < 60) return `pre ${diff} min`;
  const h = Math.round(diff / 60);
  if (h < 24) return `pre ${h} h`;
  const days = Math.round(h / 24);
  if (days < 7) return `pre ${days} d`;
  return d.toLocaleDateString("sr-RS");
}

function StateChip({ comment, userMap }) {
  const t = comment.type;
  const s = comment.state;
  if (comment.deleted_at) return null;
  if (t === "comment") return null;
  // Waiting states — highlight who
  if (s === "open" || s === "waiting") {
    const who = (comment.required_from_user_ids || [])
      .map((u) => userMap[u]?.name || "korisnika")
      .join(", ");
    const label =
      t === "question" ? "Čeka odgovor od" :
      t === "decision" ? "Čeka odluku od" :
      "Čeka odobrenje od";
    return (
      <span
        data-testid={`comment-state-${comment.id}`}
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded-md bg-ochre/10 text-ochre-ink border-ochre/30"
      >
        <Clock className="w-3 h-3" />
        {label} <b>{who}</b>
      </span>
    );
  }
  // Resolved states
  if (t === "question" && s === "answered") {
    return (
      <span data-testid={`comment-state-${comment.id}`}
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded-md bg-sage/15 text-forest border-forest/25">
        <Check className="w-3 h-3" /> Odgovoreno
      </span>
    );
  }
  if (t === "decision" && s === "decided") {
    return (
      <span data-testid={`comment-state-${comment.id}`}
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded-md bg-sage/15 text-forest border-forest/25">
        <Check className="w-3 h-3" /> Odlučeno
      </span>
    );
  }
  if (t === "approval" && s === "approved") {
    const by = userMap[comment.resolved_by_user_id]?.name;
    return (
      <span data-testid={`comment-state-${comment.id}`}
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded-md bg-sage/15 text-forest border-forest/25">
        <Check className="w-3 h-3" /> Odobreno {by ? `(${by})` : ""}
      </span>
    );
  }
  if (t === "approval" && s === "rejected") {
    const by = userMap[comment.resolved_by_user_id]?.name;
    return (
      <span data-testid={`comment-state-${comment.id}`}
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded-md bg-terracotta/12 text-terracotta border-terracotta/30">
        <X className="w-3 h-3" /> Odbijeno {by ? `(${by})` : ""}
      </span>
    );
  }
  return null;
}

export default function CommentItem({
  comment,
  replies = [],
  userMap = {},
  taskId,
  onChanged,
  depth = 0,
}) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(comment.body);
  const [replying, setReplying] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolutionText, setResolutionText] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const style = TYPE_STYLE[comment.type] || TYPE_STYLE.comment;
  const Icon = style.icon;
  const isAuthor = user?.id === comment.author_user_id;
  const isAdmin = user?.role === "admin";
  const isDeleted = !!comment.deleted_at;
  const isResolved = !!comment.resolved_at;
  const isTyped = comment.type !== "comment";
  const canResolve =
    isTyped && !isResolved && !isDeleted &&
    (comment.required_from_user_ids || []).includes(user?.id);
  const hasReactions = replies.length > 0 || isResolved || isDeleted;
  const canEdit =
    !isDeleted && isAuthor && (comment.type === "comment" || !hasReactions);
  const canDelete = !isDeleted && (isAuthor || isAdmin);

  const authorName = userMap[comment.author_user_id]?.name || "Korisnik";

  const saveEdit = async () => {
    if (!editBody.trim()) return toast.error("Upiši tekst");
    try {
      await api.patch(`/comments/${comment.id}`, { body: editBody.trim() });
      setEditing(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async (reason) => {
    try {
      const payload = isTyped ? { data: { reason } } : undefined;
      await api.delete(`/comments/${comment.id}`, payload);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  const resolveQuestion = async (replyId) => {
    try {
      await api.post(`/comments/${comment.id}/resolve`, { accepted_reply_comment_id: replyId });
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resolveDecision = async () => {
    if (!resolutionText.trim()) return toast.error("Upiši odluku");
    try {
      await api.post(`/comments/${comment.id}/resolve`, { resolution_body: resolutionText.trim() });
      setResolving(false); setResolutionText("");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resolveApproval = async (approved, reason = "") => {
    try {
      await api.post(`/comments/${comment.id}/resolve`, { approved, reason });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  return (
    <div
      data-testid={`comment-item-${comment.id}`}
      className={
        "border-l-2 " + style.bar +
        " bg-white pl-3 pr-2 py-2.5 " +
        (depth > 0 ? "ml-6 mt-1.5 border-t border-r border-b border-line rounded-r-md" : "border-t border-r border-b border-line rounded-r-md") +
        (isDeleted ? " opacity-60" : "")
      }
    >
      {/* Header line: author + time + type badge + state chip */}
      <div className="flex flex-wrap items-center gap-2 text-xs mb-1.5">
        <span className="font-medium text-ink" data-testid={`comment-author-${comment.id}`}>
          {authorName}
        </span>
        <span className="text-ink-muted">·</span>
        <span className="text-ink-muted" title={comment.created_at}>
          {fmtRelative(comment.created_at)}
        </span>
        {style.badge && (
          <span className={"inline-flex items-center gap-1 px-1.5 py-0.5 border rounded text-[10px] uppercase tracking-wider font-medium " + style.badge.cls}>
            <Icon className="w-3 h-3" /> {style.badge.text}
          </span>
        )}
        {comment.edited_at && !isDeleted && (
          <span className="text-xs text-ink-muted italic">Izmenjeno</span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <StateChip comment={comment} userMap={userMap} />
        </div>
      </div>

      {/* Body */}
      {!editing ? (
        <>
          <div
            className={"text-sm whitespace-pre-wrap " + (isDeleted ? "italic text-ink-muted" : "text-ink")}
            data-testid={`comment-body-${comment.id}`}
          >
            {comment.body}
          </div>
          {!isDeleted && comment.attachments && comment.attachments.length > 0 && (
            <AttachmentPreview attachments={comment.attachments} />
          )}
        </>
      ) : (
        <div className="space-y-2">
          <Textarea
            rows={2}
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            className="text-sm"
            data-testid={`comment-edit-textarea-${comment.id}`}
          />
          <div className="flex justify-end gap-2">
            <button className="text-xs text-ink-muted px-2 py-1" onClick={() => setEditing(false)}>Otkaži</button>
            <button
              className="text-xs bg-forest text-white px-2.5 py-1 rounded"
              onClick={saveEdit}
              data-testid={`comment-edit-save-${comment.id}`}
            >
              Sačuvaj
            </button>
          </div>
        </div>
      )}

      {/* Resolution details */}
      {isResolved && comment.type === "decision" && comment.resolution_body && (
        <div className="mt-2 text-sm text-forest bg-forest/6 border border-forest/20 rounded px-2 py-1.5" data-testid={`comment-resolution-${comment.id}`}>
          <span className="font-medium">Odluka:</span> {comment.resolution_body}
        </div>
      )}
      {isResolved && comment.type === "approval" && comment.resolution_body && (
        <div className={"mt-2 text-sm rounded px-2 py-1.5 border " +
          (comment.state === "approved"
            ? "bg-forest/6 border-forest/20 text-forest"
            : "bg-terracotta/8 border-terracotta/25 text-terracotta")}>
          <span className="font-medium">Razlog:</span> {comment.resolution_body}
        </div>
      )}

      {/* Approval quick actions inline */}
      {canResolve && comment.type === "approval" && (
        <div className="mt-2 flex gap-2">
          <button
            data-testid={`comment-approve-${comment.id}`}
            onClick={() => resolveApproval(true)}
            className="inline-flex items-center gap-1 text-xs bg-forest text-white px-2.5 py-1 rounded"
          >
            <Check className="w-3.5 h-3.5" /> Odobri
          </button>
          <button
            data-testid={`comment-reject-${comment.id}`}
            onClick={() => setRejectOpen(true)}
            className="inline-flex items-center gap-1 text-xs border border-terracotta/40 text-terracotta hover:bg-terracotta/8 px-2.5 py-1 rounded"
          >
            <X className="w-3.5 h-3.5" /> Odbij
          </button>
        </div>
      )}

      {/* Decision inline resolver */}
      {canResolve && comment.type === "decision" && !resolving && (
        <button
          data-testid={`comment-resolve-decision-open-${comment.id}`}
          onClick={() => setResolving(true)}
          className="mt-2 inline-flex items-center gap-1 text-xs bg-forest text-white px-2.5 py-1 rounded"
        >
          <GitBranch className="w-3.5 h-3.5" /> Zabeleži odluku
        </button>
      )}
      {canResolve && comment.type === "decision" && resolving && (
        <div className="mt-2 space-y-2">
          <Input
            autoFocus
            value={resolutionText}
            onChange={(e) => setResolutionText(e.target.value)}
            placeholder="Odluka (kratko i konkretno)"
            className="text-sm h-9"
            data-testid={`comment-decision-input-${comment.id}`}
          />
          <div className="flex gap-2 justify-end">
            <button className="text-xs text-ink-muted px-2" onClick={() => setResolving(false)}>Otkaži</button>
            <button
              onClick={resolveDecision}
              className="text-xs bg-forest text-white px-2.5 py-1 rounded"
              data-testid={`comment-decision-save-${comment.id}`}
            >
              Sačuvaj odluku
            </button>
          </div>
        </div>
      )}

      {/* Actions row */}
      {!isDeleted && !editing && (
        <div className="mt-2 flex items-center gap-3 text-xs text-ink-muted">
          {!isResolved && (
            <button
              onClick={() => setReplying((v) => !v)}
              data-testid={`comment-reply-btn-${comment.id}`}
              className="inline-flex items-center gap-1 hover:text-ink"
            >
              <CornerDownRight className="w-3 h-3" /> Odgovori
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => { setEditBody(comment.body); setEditing(true); }}
              data-testid={`comment-edit-btn-${comment.id}`}
              className="inline-flex items-center gap-1 hover:text-ink"
            >
              <Pencil className="w-3 h-3" /> Izmeni
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => setDeleteOpen(true)}
              data-testid={`comment-delete-btn-${comment.id}`}
              className="inline-flex items-center gap-1 hover:text-terracotta ml-auto"
            >
              <Trash2 className="w-3 h-3" /> Ukloni
            </button>
          )}
        </div>
      )}

      {/* Themed confirm dialogs (replace window.confirm / window.prompt) */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={isTyped ? `Ukloniti ovaj ${
          comment.type === "question" ? "upis pitanja" :
          comment.type === "decision" ? "upis odluke" : "upis odobrenja"
        }?` : "Ukloniti komentar?"}
        description={isTyped
          ? "Sadržaj će biti skriven, ali audit trag ostaje. Razlog je obavezan."
          : `Sadržaj će biti skriven kao "Sadržaj uklonjen". Odgovori ostaju vidljivi.`
        }
        confirmLabel="Ukloni"
        destructive
        requireReason={isTyped}
        reasonLabel="Razlog uklanjanja"
        reasonPlaceholder="npr. greška u kreiranju, dupli upis…"
        onConfirm={doDelete}
        testidPrefix={`confirm-delete-${comment.id}`}
      />
      <ConfirmDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Odbiti ovo odobrenje?"
        description="Autor će videti tvoj odgovor. Razlog nije obavezan, ali pomaže."
        confirmLabel="Odbij"
        destructive
        optionalReason
        reasonLabel="Razlog (opciono)"
        reasonPlaceholder="Zašto odbijaš?"
        onConfirm={(reason) => resolveApproval(false, reason || "")}
        testidPrefix={`confirm-reject-${comment.id}`}
      />

      {/* Reply composer */}
      {replying && (
        <div className="mt-2">
          <CommentComposer
            taskId={taskId}
            parentCommentId={comment.id}
            compact
            onPosted={() => { setReplying(false); onChanged?.(); }}
            onCancel={() => setReplying(false)}
          />
        </div>
      )}

      {/* Replies — flat 1-level rendering (per product spec: no threading) */}
      {replies.length > 0 && (
        <div className="mt-1.5 space-y-1.5" data-testid={`comment-replies-${comment.id}`}>
          {replies.map((r) => (
            <ReplyItem
              key={r.id}
              reply={r}
              userMap={userMap}
              taskId={taskId}
              parentAccept={
                canResolve && comment.type === "question" && !r.deleted_at
                  ? () => resolveQuestion(r.id)
                  : null
              }
              onChanged={onChanged}
              isAccepted={
                comment.type === "question" &&
                comment.accepted_reply_comment_id === r.id
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReplyItem({ reply, userMap, taskId, parentAccept, onChanged, isAccepted }) {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(reply.body);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isAuthor = user?.id === reply.author_user_id;
  const isAdmin = user?.role === "admin";
  const isDeleted = !!reply.deleted_at;
  const authorName = userMap[reply.author_user_id]?.name || "Korisnik";

  const saveEdit = async () => {
    if (!editBody.trim()) return toast.error("Upiši tekst");
    try {
      await api.patch(`/comments/${reply.id}`, { body: editBody.trim() });
      setEditing(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/comments/${reply.id}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  return (
    <div
      data-testid={`comment-item-${reply.id}`}
      className={
        "ml-6 border-l-2 border-l-line bg-surface-secondary/40 pl-3 pr-2 py-2 " +
        "border-t border-r border-b border-line rounded-r-md" +
        (isDeleted ? " opacity-60" : "") +
        (isAccepted ? " ring-1 ring-forest/40 bg-forest/6" : "")
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-xs mb-1">
        <CornerDownRight className="w-3 h-3 text-ink-muted" />
        <span className="font-medium text-ink">{authorName}</span>
        <span className="text-ink-muted">·</span>
        <span className="text-ink-muted">{fmtRelative(reply.created_at)}</span>
        {reply.edited_at && !isDeleted && (
          <span className="text-xs text-ink-muted italic">Izmenjeno</span>
        )}
        {isAccepted && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-forest bg-forest/10 border border-forest/25 rounded px-1.5 py-0.5">
            <Check className="w-3 h-3" /> Prihvaćeno
          </span>
        )}
      </div>
      {!editing ? (
        <div
          className={"text-sm whitespace-pre-wrap " + (isDeleted ? "italic text-ink-muted" : "text-ink")}
          data-testid={`comment-body-${reply.id}`}
        >
          {reply.body}
        </div>
      ) : (
        <div className="space-y-2">
          <Textarea rows={2} value={editBody} onChange={(e) => setEditBody(e.target.value)} className="text-sm" />
          <div className="flex justify-end gap-2">
            <button className="text-xs text-ink-muted px-2 py-1" onClick={() => setEditing(false)}>Otkaži</button>
            <button className="text-xs bg-forest text-white px-2.5 py-1 rounded" onClick={saveEdit}>Sačuvaj</button>
          </div>
        </div>
      )}
      {!isDeleted && !editing && (
        <div className="mt-1.5 flex items-center gap-3 text-xs text-ink-muted">
          {parentAccept && !isAccepted && (
            <button
              onClick={parentAccept}
              data-testid={`comment-accept-reply-${reply.id}`}
              className="inline-flex items-center gap-1 text-forest font-medium hover:underline"
            >
              <Check className="w-3 h-3" /> Označi kao prihvaćen odgovor
            </button>
          )}
          {isAuthor && (
            <button onClick={() => { setEditBody(reply.body); setEditing(true); }} className="inline-flex items-center gap-1 hover:text-ink">
              <Pencil className="w-3 h-3" /> Izmeni
            </button>
          )}
          {(isAuthor || isAdmin) && (
            <button onClick={() => setDeleteOpen(true)} className="inline-flex items-center gap-1 hover:text-terracotta ml-auto" data-testid={`comment-delete-btn-${reply.id}`}>
              <Trash2 className="w-3 h-3" /> Ukloni
            </button>
          )}
        </div>
      )}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Ukloniti ovaj odgovor?"
        description={`Sadržaj će biti skriven kao "Sadržaj uklonjen".`}
        confirmLabel="Ukloni"
        destructive
        onConfirm={doDelete}
        testidPrefix={`confirm-delete-${reply.id}`}
      />
    </div>
  );
}
